import { RefObject, useRef } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';
import { OptimizationWorkload } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const WOOP_WORKLOADS_LIST_QUERY_KEY = 'woop-workloads-list';

export const getWorkloadsQueryKey = ({
  clusterId,
  tourStepId,
}: { clusterId?: string; tourStepId?: string | null } = {}) => {
  if (clusterId && tourStepId) {
    return [WOOP_WORKLOADS_LIST_QUERY_KEY, clusterId, tourStepId] as const;
  }
  if (clusterId) {
    return [WOOP_WORKLOADS_LIST_QUERY_KEY, clusterId] as const;
  }
  return [WOOP_WORKLOADS_LIST_QUERY_KEY] as const;
};

type UseWorkloadsQueryArgs = {
  clusterId: string;
  enabled?: boolean;
  demoFetchIndex?: number;
  syncGroup?: string;
};

const getWorkloadsQueryOptions = (
  {
    clusterId,
    enabled,
    syncGroup,
  }: Omit<UseWorkloadsQueryArgs, 'demoFetchIndex'>,
  demoFetchIndexRef: RefObject<number | undefined>,
  tourStepId: string | null
) =>
  queryOptions<OptimizationWorkload[]>({
    queryKey: getWorkloadsQueryKey({ clusterId, tourStepId }),
    queryFn: async () => {
      const { data } = await apiClient.workloadOptimization.getWorkloads(
        {
          clusterId,
        },
        {
          timeout: 60_000,
          middlewareArgs: {
            requestMeta: { demoFetchIndex: demoFetchIndexRef.current },
          },
        }
      );
      return data.workloads || [];
    },
    enabled,
    meta: {
      syncGroup: syncGroup,
    },
  });

export const useWorkloadsQuery = ({
  clusterId,
  enabled,
  demoFetchIndex,
  syncGroup,
}: UseWorkloadsQueryArgs) => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);
  // Use ref to prevent stale closure
  const demoFetchIndexRef = useRef(demoFetchIndex);
  demoFetchIndexRef.current = demoFetchIndex;

  const { isLoading, data, error, refetch } = useQuery(
    getWorkloadsQueryOptions(
      { clusterId, enabled, syncGroup },
      demoFetchIndexRef,
      tourStepId
    )
  );

  return { isLoading, workloads: data, error, refetch };
};
